/**
 * AreaAnalysisCard
 * 
 * @Author: Focci
 * @Date: 2023-08-11 17:20:27
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-11 17:20:27
 */

'use client'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import Link from 'next/link'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import SVGDollar from '@img/dollar.svg'
import SVGValue from '@img/value.svg'
import useTracking from '@hook/useTracking'
import { useCallback, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { formatPrice, toSlug } from '@lib/utils'
import { houseSearchType } from '@lib/config'
import { urls } from '@lib/parse'

export function AreaAnalysisCard({
  Icon,
  label = '',
  value = '',
  meta = '',
  link = '',
  className = '',
  onClick = () => {}
}) {
  const th = useTranslations('house')
  const ts = useTranslations('search')

  return (
    <div 
      className={clsx([
        'flex items-center gap-x-2 px-3 py-4 rounded-sm',
        className
      ])}
    >
      <div className="shrink-0">
        {Icon}
      </div>
      <div className="flex-auto flex flex-col gap-0.5">
        <div>
          {label}
          <span className="text-meta ml-1">
            {`(${th('last12Month')})`}
          </span>
        </div>
        <div className="text-f1.2 font-bold flex items-center">
          {value}
          {link && (
            <Link 
              href={link}
              onClick={onClick}
              className="inline-flex items-center text-primary px-2 text-f.7 font-normal"
            >
              {ts('view')}
              <ArrowForwardIosIcon sx={{ fontSize: '.7rem' }} />
            </Link>
          )}
        </div>
        {meta}
      </div>
    </div>
  )
}

export default function AreaAnalysisCards({
  house,
  data = {}
}) {
  const locale = useLocale()
  const th = useTranslations('house')
  const { pushTrackListingClick } = useTracking()
  
  const url = useMemo(() => {
    if(data.suburb && data.suburb_id) {
      const urlParam = {
        page: 1,
        suburb: [{ label: toSlug(data.suburb), value: data.suburb_id }]
      }
      
      return `${urls.listing(houseSearchType.sold, urlParam, locale)}`
    }

    return ''
  }, [data, locale])

  const handleClick = useCallback(() => {
    pushTrackListingClick('suburbs-view', house)
  }, [house, pushTrackListingClick])

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 border border-ce6">
      <AreaAnalysisCard
        Icon={<SVGDollar className="text-primary" />}
        label={th('numberOfSales')}
        value={data.sale_count}
        meta={`${th('propertyType')}: ${data.title}`}
        link={url}
        onClick={handleClick}
      />
      <AreaAnalysisCard
        Icon={<SVGValue />}
        className="border-t xl:border-l xl:border-t-0 border-ce6"
        label={th('medianSalesPrice')}
        value={data.sale_median ? `$${formatPrice(data.sale_median)}` : '-'}
        meta={
          (data.sale_low || data.sale_high)
            ? `${th('low')}: ${`$${formatPrice(data.sale_low) || ''}`}, ${th('high')}: ${`$${formatPrice(data.sale_high) || ''}`}`
            : null
        }
      />
      <AreaAnalysisCard
        Icon={<SVGValue />}
        className="border-t xl:border-l xl:border-t-0 border-ce6"
        label={th('medianRentPrice')}
        value={data.rent_median ? `$${formatPrice(data.rent_median)}` : '-'}
        meta={
          (data.rent_low || data.rent_high)
            ? `${th('low')}: ${`$${formatPrice(data.rent_low) || ''}`}, ${th('high')}: ${`$${formatPrice(data.rent_high) || ''}`}`
            : null
        }
      />
    </div>
  )
}

AreaAnalysisCards.propTypes = {
  data: PropTypes.object,
}
